<script setup>
import { ref, computed, onMounted, watch, h } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
    Card,
    Table,
    Button,
    Spin,
    Tag,
    message,
    Modal,
    Alert,
    Empty
} from 'ant-design-vue';
import {
    ArrowLeftOutlined,
    HistoryOutlined
} from '@ant-design/icons-vue';
import {
    getConsultantClient,
    createConsultantClientVersion,
    completeConsultationBooking,
    noShowConsultationBooking,
    getConsultationWorkspace
} from '@/api/consultation-workspace/consultationWorkspaceApi';
import { getConsultationBookingStatusMeta } from '@/utils/consultationBookingStatus';
import { canCompleteConsultationWorkflow, resolveWorkflowSourceAction } from '@/utils/versionWorkflow';
import {
    getBalanceSheetStatusMeta,
    formatVersionDisplayName,
    formatVersionDateTime,
    formatSheetAmount
} from '@/utils/balanceSheetDisplay';

const route = useRoute();
const router = useRouter();
const userId = computed(() => route.params.userId);
const consultationId = computed(() => {
    const raw = Number(route.query.consultation_id);
    return Number.isFinite(raw) && raw > 0 ? raw : null;
});
const isBookingSession = computed(() => consultationId.value != null);

const loading = ref(false);
const user = ref(null);
const versions = ref([]);
const consultations = ref([]);
const versionWindow = ref(null);
const assetSummary = ref(null);
const workflow = ref(null);
const workflowLoading = ref(false);
const startingConsultation = ref(false);
const selectedVersionId = ref(null);
const endingConsultation = ref(false);

const workflowStarted = computed(() => Boolean(workflow.value?.stage || workflow.value?.inventory));
const isConsultationSourceLocked = computed(
    () =>
        ['inventory_submitted', 'post_draft', 'consultation_submitted'].includes(
            workflow.value?.stage
        ) || Boolean(workflow.value?.inventory?.locked)
);
const isWorkflowEditing = computed(() =>
    ['inventory_draft', 'inventory_submitted', 'post_draft'].includes(workflow.value?.stage)
);
const lockedSourceVersion = computed(() =>
    versions.value.find((version) => version.is_inventory_source)
);

const selectedVersion = computed(
    () => versions.value.find((v) => Number(v.id) === Number(selectedVersionId.value)) || null
);

const workflowSteps = computed(() => {
    const stage = workflow.value?.stage;
    const confirmed = stage === 'consultation_submitted';
    return [
        {
            key: 'original',
            title: '原始版',
            subtitle: '學員提供',
            sheet: workflow.value?.original,
            locked: true,
            done: Boolean(workflow.value?.original)
        },
        {
            key: 'inventory',
            title: '盤點版',
            subtitle: '諮詢師盤點',
            sheet: workflow.value?.inventory,
            locked: confirmed,
            done: Boolean(workflow.value?.inventory)
        },
        {
            key: 'consultation',
            title: '諮詢版',
            subtitle: '共同微調／確認',
            sheet: workflow.value?.post_consultation,
            locked: confirmed,
            done: confirmed
        },
        {
            key: 'conclusion',
            title: '結論',
            subtitle: '報告定版',
            sheet: null,
            locked: confirmed,
            done: confirmed
        }
    ];
});

const startConsultationLabel = computed(() => {
    if (!workflowStarted.value) return '開始諮詢';
    if (isRestartFromOtherVersion.value) return '改從此版建立盤點';
    if (workflow.value?.stage === 'consultation_submitted') return '查看結論';
    if (workflow.value?.stage === 'post_draft') return '繼續諮詢版';
    if (workflow.value?.stage === 'inventory_draft') return '繼續盤點';
    if (workflow.value?.stage === 'inventory_submitted') return '進入諮詢版';
    return '繼續諮詢';
});

const canFinishConsultation = computed(() =>
    canCompleteConsultationWorkflow(workflow.value || {})
);

const currentWorkflowRootId = computed(() => {
    const fromInventoryParent = workflow.value?.inventory?.source_balance_sheet_id;
    if (fromInventoryParent) return Number(fromInventoryParent);
    if (workflow.value?.original?.id) return Number(workflow.value.original.id);
    if (activeConsultation.value?.version_id) return Number(activeConsultation.value.version_id);
    return null;
});

const isRestartFromOtherVersion = computed(() => {
    if (!workflowStarted.value || !selectedVersionId.value || !currentWorkflowRootId.value) {
        return false;
    }
    return Number(selectedVersionId.value) !== Number(currentWorkflowRootId.value);
});

const canSelectVersion = computed(
    () => activeConsultation.value?.status === 'confirmed'
);

const bookingVersionColumns = [
    { title: '版本', key: 'display_name', width: 180 },
    { title: '狀態', key: 'flags', width: 140 },
    { title: '資產', key: 'total_assets', width: 110, align: 'right' },
    { title: '淨資產', key: 'net_asset_value', width: 110, align: 'right' },
    { title: '收入', key: 'total_monthly_income', width: 110, align: 'right' },
    { title: '支出', key: 'total_monthly_expenses', width: 110, align: 'right' },
    { title: '建立', dataIndex: 'created_at', key: 'created_at', width: 140 },
    { title: '操作', key: 'action', width: 150, fixed: 'right' }
];

/** 版本列表用短名（時間另有欄位，不重複塞進名稱） */
const shortVersionName = (version) => {
    const name = String(version?.name || '').trim();
    if (name && !/^\d+$/.test(name)) return name;
    return formatVersionDateTime(version?.created_at) || '未命名版本';
};

/** 場次版本列的短狀態（最多顯示主要 1～2 個） */
const versionFlagTags = (record) => {
    const tags = [];
    if (record.is_inventory_source && isWorkflowEditing.value) {
        tags.push({ key: 'active', color: 'orange', label: '進行中' });
    } else if (record.is_booking_version) {
        tags.push({ key: 'apply', color: 'blue', label: '申請' });
    }
    if (record.is_latest) {
        tags.push({ key: 'latest', color: 'green', label: '最新' });
    }
    if (record.has_consultation) {
        tags.push({ key: 'done', color: 'gold', label: '已諮詢' });
    }
    const sheet = getBalanceSheetStatusMeta(record.status);
    if (sheet.label === '編輯中' && !tags.some((t) => t.key === 'active')) {
        tags.push({ key: 'draft', color: 'processing', label: '草稿' });
    } else if (sheet.label === '完成' && !tags.some((t) => t.key === 'done')) {
        tags.push({ key: 'done-sheet', color: 'default', label: '完成' });
    }
    return tags.slice(0, 2);
};

const browseVersionColumns = [
    { title: '版本名稱', key: 'display_name', width: 220 },
    { title: '資產', key: 'total_assets', width: 110, align: 'right' },
    { title: '淨資產', key: 'net_asset_value', width: 120, align: 'right' },
    { title: '收入', key: 'total_monthly_income', width: 100, align: 'right' },
    { title: '支出', key: 'total_monthly_expenses', width: 100, align: 'right' },
    { title: '建立時間', dataIndex: 'created_at', key: 'created_at', width: 140 },
    { title: '操作', key: 'action', width: 140 }
];

/** 金額語意色：收入偏綠、支出偏紅、淨資產依正負 */
const amountToneClass = (value, { asExpense = false } = {}) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num === 0) return 'amount-cell--neutral';
    if (asExpense) return 'amount-cell--negative';
    return num > 0 ? 'amount-cell--positive' : 'amount-cell--negative';
};

const activeConsultation = computed(
    () => consultations.value.find((c) => c.id === consultationId.value) || null
);

const upcomingConsultations = computed(() =>
    consultations.value
        .filter((c) => c.status === 'confirmed')
        .slice()
        .sort((a, b) => String(a.starts_at || '').localeCompare(String(b.starts_at || '')))
);

const isVersionEditable = (status) => status === '編輯中' || status === '0';

const load = async () => {
    loading.value = true;
    try {
        const params = consultationId.value ? { consultation_id: consultationId.value } : {};
        const res = await getConsultantClient(userId.value, params);
        if (res.status === 1) {
            user.value = res.data.user;
            versions.value = (res.data.versions || []).map((v) => ({ ...v, key: v.id }));
            consultations.value = (res.data.consultations || []).map((c) => ({
                ...c,
                key: c.id
            }));
            versionWindow.value = res.data.version_window || null;
            assetSummary.value = res.data.asset_summary || null;

            const preferred =
                versions.value.find((v) => v.is_booking_version)?.id ||
                versions.value.find((v) => v.is_latest)?.id ||
                versions.value[0]?.id ||
                null;
            if (
                !selectedVersionId.value ||
                !versions.value.some((v) => Number(v.id) === Number(selectedVersionId.value))
            ) {
                selectedVersionId.value = preferred != null ? Number(preferred) : null;
            }

            if (consultationId.value) await loadWorkflow();
            else workflow.value = null;

            // 已開始時預選目前流程鏈的原始版；尚未開始則沿用申請版／最新
            if (workflow.value?.inventory || workflow.value?.stage) {
                const rootId =
                    Number(workflow.value?.inventory?.source_balance_sheet_id) ||
                    Number(workflow.value?.original?.id) ||
                    null;
                if (rootId && versions.value.some((v) => Number(v.id) === rootId)) {
                    selectedVersionId.value = rootId;
                }
            }
        }
    } finally {
        loading.value = false;
    }
};

const loadWorkflow = async () => {
    if (!consultationId.value) return;
    workflowLoading.value = true;
    try {
        const res = await getConsultationWorkspace(consultationId.value);
        if (res.status === 1) workflow.value = res.data;
    } finally {
        workflowLoading.value = false;
    }
};

const enterBookingSession = (id) => {
    router.push({
        path: `/consultation-workspace/clients/${userId.value}`,
        query: { consultation_id: id }
    });
};

const startWorkflow = async (basedOnVersionId = null, restart = false) => {
    const originalId = Number(
        basedOnVersionId ||
            selectedVersionId.value ||
            workflow.value?.original?.id ||
            activeConsultation.value?.version_id
    );
    if (!originalId) return message.warning('請先選擇要作為諮詢來源的版本');
    workflowLoading.value = true;
    startingConsultation.value = true;
    try {
        const payload = {
            based_on_version_id: originalId,
            source: 1,
            external_name: '盤點版'
        };
        if (consultationId.value) payload.consultation_id = consultationId.value;
        const res = await createConsultantClientVersion(userId.value, payload);
        if (res.status === 1) {
            const restarting = restart;
            await load();
            message.success(restarting ? '已改從此版建立盤點' : '已開始諮詢，進入盤點版');
            const inventoryId = Number(res.data?.id || workflow.value?.inventory?.id);
            if (inventoryId) openVersionEdit(inventoryId);
        } else {
            message.error(res.message || '建立盤點版失敗');
        }
    } finally {
        workflowLoading.value = false;
        startingConsultation.value = false;
    }
};

const startConsultation = async () => {
    if (!isBookingSession.value || !activeConsultation.value) return;
    if (activeConsultation.value.status !== 'confirmed') {
        return message.warning('此預約已結束，無法開始諮詢');
    }

    if (!selectedVersionId.value) {
        return message.warning('請先選取版本');
    }

    // 已開始且仍選同一條鏈：直接進入目前盤點／諮詢版
    if (workflowStarted.value && !isRestartFromOtherVersion.value) {
        const stage = workflow.value?.stage;
        if (stage === 'consultation_submitted') {
            openWorkflowStep(workflowSteps.value[3]);
            return;
        }
        if (stage === 'post_draft' || stage === 'inventory_submitted') {
            const id = workflow.value?.post_consultation?.id || workflow.value?.inventory?.id;
            if (id) openVersionEdit(id);
            return;
        }
        if (workflow.value?.inventory?.id) {
            openVersionEdit(workflow.value.inventory.id);
            return;
        }
        return;
    }

    const name = formatVersionDisplayName(selectedVersion.value) || '所選版本';
    const restart = isRestartFromOtherVersion.value;
    Modal.confirm({
        title: restart ? '改從此版建立盤點？' : '開始諮詢？',
        icon: null,
        centered: true,
        width: 440,
        class: 'cw-confirm-modal',
        wrapClassName: 'cw-confirm-modal-wrap',
        okText: restart ? '建立盤點' : '開始諮詢',
        cancelText: '取消',
        okButtonProps: { class: 'cw-confirm-modal__ok' },
        cancelButtonProps: { class: 'cw-confirm-modal__cancel' },
        content: h('div', { class: 'cw-confirm-modal__body' }, [
            h(
                'p',
                { class: 'cw-confirm-modal__lead' },
                restart
                    ? '將另建一份盤點版；先前盤點／諮詢版會保留。'
                    : '確認後會依所選版本建立盤點版，並進入編輯。'
            ),
            h('div', { class: 'cw-confirm-modal__chip' }, name)
        ]),
        async onOk() {
            await startWorkflow(selectedVersionId.value, restart);
        }
    });
};

const handleVersionConsultation = (record) => {
    const action = resolveWorkflowSourceAction(record, workflow.value);
    if (action.mode === 'conclusion') {
        openWorkflowStep(workflowSteps.value[3]);
        return;
    }
    if (action.mode === 'edit') {
        openVersionEdit(action.versionId);
        return;
    }
    if (action.mode === 'view') {
        openVersionView(action.versionId);
        return;
    }
    if (action.mode === 'locked') return;

    const replacingDraft = workflow.value?.stage === 'inventory_draft';
    const name = formatVersionDisplayName(record) || '所選版本';
    Modal.confirm({
        title: replacingDraft ? '改從此版本建立盤點？' : '使用此版本開始諮詢？',
        content: replacingDraft
            ? `將刪除目前尚未完成的盤點版，改以「${name}」建立新的盤點版。`
            : `將以「${name}」建立本次諮詢的盤點版。`,
        okText: replacingDraft ? '更換並建立盤點' : '開始諮詢',
        cancelText: '取消',
        async onOk() {
            await startWorkflow(record.id, replacingDraft);
        }
    });
};

const openWorkflowStep = (step) => {
    const currentConsultationId = consultationId.value || activeConsultation.value?.id;
    if (
        step.key === 'conclusion' &&
        currentConsultationId &&
        workflow.value?.stage === 'consultation_submitted'
    ) {
        router.push({
            name: 'ConsultationWorkspaceConclusion',
            params: {
                userId: userId.value,
                version: workflow.value.post_consultation?.id || workflow.value.inventory.id
            },
            query: { consultation_id: currentConsultationId }
        });
        return;
    }
    if (!step.sheet?.id) return;
    step.locked ? openVersionView(step.sheet.id) : openVersionEdit(step.sheet.id);
};

const goBack = () => {
    if (isBookingSession.value) {
        router.push('/consultation-workspace/bookings');
        return;
    }
    router.push('/consultation-workspace/clients');
};

const openInNewTab = (routeLocation) => {
    const { href } = router.resolve(routeLocation);
    window.open(href, '_blank', 'noopener,noreferrer');
};

const openVersionView = (versionId) => {
    const query = {};
    if (consultationId.value) query.consultation_id = consultationId.value;
    if (userId.value) query.user_id = userId.value;
    openInNewTab({
        name: 'ConsultationWorkspaceVersionView',
        params: { id: versionId },
        query
    });
};

const openVersionEdit = (versionId) => {
    const query = {};
    if (consultationId.value) query.consultation_id = consultationId.value;
    if (userId.value) query.user_id = userId.value;
    openInNewTab({
        name: 'ConsultationWorkspaceVersionEdit',
        params: { id: versionId },
        query
    });
};

const finishConsultation = async () => {
    if (!activeConsultation.value || endingConsultation.value) return;
    if (!canFinishConsultation.value) {
        message.warning('請先完成諮詢版，再結束諮詢');
        return;
    }
    const confirmed = await new Promise((resolve) => {
        Modal.confirm({
            title: '結束諮詢',
            icon: null,
            centered: true,
            width: 420,
            class: 'cw-confirm-modal',
            wrapClassName: 'cw-confirm-modal-wrap',
            content: h('div', { class: 'cw-confirm-modal__body' }, [
                h('p', { class: 'cw-confirm-modal__lead' }, '確定要結束本次諮詢？')
            ]),
            okText: '結束諮詢',
            cancelText: '取消',
            okButtonProps: { class: 'cw-confirm-modal__ok' },
            cancelButtonProps: { class: 'cw-confirm-modal__cancel' },
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
        });
    });
    if (!confirmed) return;

    endingConsultation.value = true;
    try {
        const res = await completeConsultationBooking(activeConsultation.value.id, {});
        if (res.status === 1) {
            message.success('已結束諮詢');
            await load();
        } else {
            message.error(res.message || '結束諮詢失敗');
        }
    } catch (e) {
        message.error(e?.message || '操作失敗');
    } finally {
        endingConsultation.value = false;
    }
};

const markNoShow = async () => {
    if (!activeConsultation.value || endingConsultation.value) return;
    const confirmed = await new Promise((resolve) => {
        Modal.confirm({
            title: '標記未到',
            icon: null,
            centered: true,
            width: 420,
            class: 'cw-confirm-modal',
            wrapClassName: 'cw-confirm-modal-wrap',
            content: h('div', { class: 'cw-confirm-modal__body' }, [
                h('p', { class: 'cw-confirm-modal__lead' }, '確定將此預約標記為學員未到？')
            ]),
            okText: '標記未到',
            okType: 'danger',
            cancelText: '取消',
            okButtonProps: { danger: true, class: 'cw-confirm-modal__danger' },
            cancelButtonProps: { class: 'cw-confirm-modal__cancel' },
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
        });
    });
    if (!confirmed) return;

    endingConsultation.value = true;
    try {
        const res = await noShowConsultationBooking(activeConsultation.value.id, {});
        if (res.status === 1) {
            message.success('已標記未到');
            await load();
        } else {
            message.error(res.message || '標記未到失敗');
        }
    } catch (e) {
        message.error(e?.message || '操作失敗');
    } finally {
        endingConsultation.value = false;
    }
};

watch(
    () => [route.params.userId, route.query.consultation_id],
    () => load()
);

onMounted(load);
</script>

<template>
    <Spin :spinning="loading">
        <div class="cw-client-detail">
            <header class="cw-client-topbar">
                <div class="cw-client-topbar__nav">
                    <button type="button" class="cw-client-topbar__back" @click="goBack">
                        <ArrowLeftOutlined />
                        {{ isBookingSession ? '我的預約' : '學員列表' }}
                    </button>
                    <span class="cw-client-topbar__sep">/</span>
                    <span class="cw-client-topbar__title">
                        {{ isBookingSession ? '諮詢場次' : '學員詳情' }}
                    </span>
                    <Tag v-if="user?.name" class="cw-client-topbar__tag">{{ user.name }}</Tag>
                </div>
                <Button
                    v-if="user"
                    type="link"
                    class="cw-client-topbar__history"
                    @click="router.push(`/consultation-workspace/clients/${userId}/consultations`)"
                >
                    <HistoryOutlined />
                    諮詢紀錄
                </Button>
            </header>

            <!-- ========== 從預約進入 ========== -->
            <template v-if="isBookingSession && activeConsultation">
                <Card class="mb-4 admin-panel cw-booking-card" :bordered="false">
                    <div class="cw-booking-card__head">
                        <div>
                            <div class="cw-booking-card__eyebrow">本次預約場次</div>
                            <div class="cw-booking-card__meta">
                                <Tag
                                    :color="
                                        getConsultationBookingStatusMeta(activeConsultation.status)
                                            .color
                                    "
                                    class="cw-booking-card__status"
                                >
                                    {{
                                        getConsultationBookingStatusMeta(activeConsultation.status)
                                            .label
                                    }}
                                </Tag>
                                <span class="cw-booking-card__time">
                                    {{ activeConsultation.starts_at }}
                                    <span class="cw-booking-card__dot">·</span>
                                    {{ activeConsultation.mode === 'offline' ? '實體' : '線上' }}
                                </span>
                            </div>
                        </div>
                        <div v-if="activeConsultation.version_name" class="cw-booking-card__version">
                            <span class="cw-booking-card__version-label">申請版本</span>
                            <span class="cw-booking-card__version-name">
                                {{ activeConsultation.version_name }}
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="activeConsultation.status === 'confirmed'"
                        class="cw-booking-card__cta-row"
                    >
                        <div class="cw-booking-card__cta-actions">
                            <Button
                                class="cw-btn cw-btn--secondary"
                                :loading="endingConsultation"
                                :disabled="!canFinishConsultation"
                                :title="canFinishConsultation ? '結束諮詢' : '請先完成諮詢版'"
                                @click="finishConsultation"
                            >
                                結束諮詢
                            </Button>
                            <Button
                                danger
                                class="cw-btn cw-btn--danger"
                                :loading="endingConsultation"
                                @click="markNoShow"
                            >
                                標記未到
                            </Button>
                        </div>
                    </div>
                </Card>

                <Card class="mb-4 admin-panel cw-versions-card" :bordered="false">
                    <template #title>
                        <div class="cw-versions-card__title-row">
                            <span>可選版本</span>
                            <span v-if="versionWindow" class="cw-versions-card__window">
                                {{ formatVersionDateTime(versionWindow.start) }}
                                ～
                                {{ formatVersionDateTime(versionWindow.end) }}
                            </span>
                        </div>
                    </template>
                    <Alert
                        v-if="isConsultationSourceLocked && lockedSourceVersion"
                        type="info"
                        show-icon
                        class="mb-3"
                        message="來源版本已鎖定"
                        :description="`盤點版已完成，本次只能繼續「${shortVersionName(lockedSourceVersion)}」；其他版本無法切換。`"
                    />

                    <Table
                        v-if="versions.length"
                        :columns="bookingVersionColumns"
                        :data-source="versions"
                        :pagination="false"
                        :scroll="{ x: 1080 }"
                        size="middle"
                        :row-class-name="
                            (record) =>
                                record.is_inventory_source
                                    ? 'cw-version-row--selected'
                                    : ''
                        "
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'display_name'">
                                <span class="version-display-name" :title="formatVersionDisplayName(record)">
                                    {{ shortVersionName(record) }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'flags'">
                                <div class="version-tags">
                                    <template v-if="versionFlagTags(record).length">
                                        <Tag
                                            v-for="tag in versionFlagTags(record)"
                                            :key="tag.key"
                                            :color="tag.color"
                                        >
                                            {{ tag.label }}
                                        </Tag>
                                    </template>
                                    <span v-else class="text-slate-400">—</span>
                                </div>
                            </template>
                            <template v-else-if="column.key === 'created_at'">
                                <span class="version-created">
                                    {{ formatVersionDateTime(record.created_at) }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'total_assets'">
                                <span
                                    class="amount-cell"
                                    :class="amountToneClass(record.total_assets)"
                                >{{ formatSheetAmount(record.total_assets) }}</span>
                            </template>
                            <template v-else-if="column.key === 'net_asset_value'">
                                <span
                                    class="amount-cell"
                                    :class="amountToneClass(record.net_asset_value)"
                                >{{ formatSheetAmount(record.net_asset_value) }}</span>
                            </template>
                            <template v-else-if="column.key === 'total_monthly_income'">
                                <span
                                    class="amount-cell"
                                    :class="amountToneClass(record.total_monthly_income)"
                                >{{ formatSheetAmount(record.total_monthly_income) }}</span>
                            </template>
                            <template v-else-if="column.key === 'total_monthly_expenses'">
                                <span
                                    class="amount-cell"
                                    :class="
                                        amountToneClass(record.total_monthly_expenses, {
                                            asExpense: true
                                        })
                                    "
                                >{{ formatSheetAmount(record.total_monthly_expenses) }}</span>
                            </template>
                            <template v-else-if="column.key === 'action'">
                                <Button
                                    type="primary"
                                    :ghost="!record.is_inventory_source"
                                    size="small"
                                    class="admin-table-actions__btn"
                                    :loading="startingConsultation || workflowLoading"
                                    :disabled="
                                        resolveWorkflowSourceAction(record, workflow).mode ===
                                        'locked'
                                    "
                                    @click.stop="handleVersionConsultation(record)"
                                >
                                    {{
                                        resolveWorkflowSourceAction(record, workflow).label
                                    }}
                                </Button>
                            </template>
                        </template>
                    </Table>
                    <Empty v-else description="此時間窗內沒有可見版本" />
                </Card>
            </template>

            <Card
                v-else-if="isBookingSession && !loading"
                class="mb-4 admin-panel"
                :bordered="false"
            >
                <p class="text-muted mb-0">找不到此次預約，請返回我的預約重新進入。</p>
            </Card>

            <!-- ========== 從學員列表進入 ========== -->
            <template v-else-if="!isBookingSession">
                <Card class="mb-4 admin-panel cw-browse-card" :bordered="false">
                    <div class="cw-browse-card__head">
                        <div>
                            <div class="cw-browse-card__eyebrow">學員總覽</div>
                            <h2 class="cw-browse-card__title">{{ user?.name || '學員' }}</h2>
                            <p class="cw-browse-card__desc">先進入待諮詢預約，再選版本。</p>
                        </div>
                    </div>

                    <div v-if="upcomingConsultations.length" class="cw-upcoming-list">
                        <div
                            v-for="item in upcomingConsultations"
                            :key="item.id"
                            class="cw-upcoming-item"
                        >
                            <div class="cw-upcoming-item__main">
                                <div class="cw-upcoming-item__time">{{ item.starts_at }}</div>
                                <div class="cw-upcoming-item__meta">
                                    {{ item.mode === 'offline' ? '實體' : '線上' }}
                                    <template v-if="item.version_name">
                                        · 申請版 {{ item.version_name }}
                                    </template>
                                </div>
                            </div>
                            <Tag :color="getConsultationBookingStatusMeta(item.status).color">
                                {{ getConsultationBookingStatusMeta(item.status).label }}
                            </Tag>
                            <Button
                                type="primary"
                                class="cw-btn cw-btn--primary"
                                @click="enterBookingSession(item.id)"
                            >
                                進入場次
                            </Button>
                        </div>
                    </div>
                    <Empty v-else description="目前沒有待諮詢預約" />
                </Card>

                <Card title="學員版本" class="admin-panel cw-versions-card" :bordered="false">
                    <Table
                        v-if="versions.length"
                        :columns="browseVersionColumns"
                        :data-source="versions"
                        :pagination="false"
                        size="middle"
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'display_name'">
                                <div class="version-name-cell">
                                    <div class="version-name-cell__row">
                                        <span
                                            class="version-display-name"
                                            :title="formatVersionDisplayName(record)"
                                        >
                                            {{ shortVersionName(record) }}
                                        </span>
                                        <Tag
                                            :color="
                                                getBalanceSheetStatusMeta(record.status).color
                                            "
                                            class="version-inline-tag"
                                        >
                                            {{ getBalanceSheetStatusMeta(record.status).label }}
                                        </Tag>
                                        <Tag
                                            v-if="record.has_consultation"
                                            color="gold"
                                            class="version-inline-tag"
                                        >
                                            已諮詢
                                        </Tag>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="column.key === 'total_assets'">
                                <span
                                    class="amount-cell"
                                    :class="amountToneClass(record.total_assets)"
                                >
                                    {{ formatSheetAmount(record.total_assets) }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'net_asset_value'">
                                <span
                                    class="amount-cell"
                                    :class="amountToneClass(record.net_asset_value)"
                                >
                                    {{ formatSheetAmount(record.net_asset_value) }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'total_monthly_income'">
                                <span
                                    class="amount-cell"
                                    :class="amountToneClass(record.total_monthly_income)"
                                >
                                    {{ formatSheetAmount(record.total_monthly_income) }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'total_monthly_expenses'">
                                <span
                                    class="amount-cell"
                                    :class="
                                        amountToneClass(record.total_monthly_expenses, {
                                            asExpense: true
                                        })
                                    "
                                >
                                    {{ formatSheetAmount(record.total_monthly_expenses) }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'created_at'">
                                <span class="version-created">
                                    {{ formatVersionDateTime(record.created_at) }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'action'">
                                <div class="admin-table-actions">
                                    <Button
                                        size="small"
                                        class="admin-table-actions__btn"
                                        @click="openVersionView(record.id)"
                                    >
                                        檢視
                                    </Button>
                                    <Button
                                        v-if="isVersionEditable(record.status)"
                                        type="primary"
                                        ghost
                                        size="small"
                                        class="admin-table-actions__btn"
                                        @click="openVersionEdit(record.id)"
                                    >
                                        編輯
                                    </Button>
                                </div>
                            </template>
                        </template>
                    </Table>
                    <Empty v-else description="尚無版本" />
                </Card>
            </template>
        </div>
    </Spin>
</template>

<style scoped>
.cw-client-detail {
    width: 100%;
    max-width: none;
}
.mb-4 {
    margin-bottom: 14px;
}
.text-muted {
    color: #64748b;
}
.cw-client-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
}
.cw-client-topbar__nav {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
}
.cw-client-topbar__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #8b6914;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.cw-client-topbar__back:hover {
    color: #6b5428;
}
.cw-client-topbar__sep {
    color: #cbd5e1;
    font-size: 12px;
}
.cw-client-topbar__title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}
.cw-client-topbar__tag {
    margin: 0;
    border-radius: 999px;
    font-weight: 600;
}
.cw-client-topbar__history {
    padding-inline: 8px;
    color: #8b6914 !important;
    font-weight: 600;
}
.cw-booking-card__head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}
.cw-booking-card__eyebrow,
.cw-browse-card__eyebrow {
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #64748b;
}
.cw-booking-card__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}
.cw-booking-card__status {
    margin: 0;
}
.cw-booking-card__time {
    font-size: 14px;
    color: #334155;
}
.cw-booking-card__dot {
    margin: 0 6px;
    color: #94a3b8;
}
.cw-booking-card__version {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
}
.cw-booking-card__version-label {
    font-size: 12px;
    color: #64748b;
}
.cw-booking-card__version-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
}
.cw-booking-card__version-link {
    height: auto;
    padding: 0;
}
.cw-booking-card__cta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-top: 14px;
    border-top: 1px solid #f1f5f9;
}
.cw-booking-card__cta-main {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    flex: 1;
}
.cw-booking-card__cta-hint {
    margin: 0;
    max-width: 520px;
    font-size: 12px;
    line-height: 1.5;
    color: #64748b;
}
.cw-booking-card__cta-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.cw-btn--start {
    min-width: 140px;
}
.cw-browse-card__title {
    margin: 0 0 6px;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
}
.cw-browse-card__desc {
    margin: 0;
    font-size: 13px;
    color: #64748b;
}
.cw-upcoming-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 16px;
}
.cw-upcoming-item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid #e8edf2;
    border-radius: 12px;
    background: #fafbfc;
}
.cw-upcoming-item__main {
    flex: 1;
    min-width: 180px;
}
.cw-upcoming-item__time {
    font-size: 14px;
    font-weight: 700;
    color: #8b6914;
}
.cw-upcoming-item__meta {
    margin-top: 2px;
    font-size: 12px;
    color: #64748b;
}
.cw-versions-card :deep(.ant-card-head-title) {
    font-weight: 700;
}
.cw-versions-card__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px 12px;
}
.cw-versions-card__window {
    font-size: 12px;
    font-weight: 500;
    color: #94a3b8;
    letter-spacing: 0;
}
.versions-hint {
    margin: 0 0 12px;
    font-size: 13px;
    color: #64748b;
}
.cw-versions-card :deep(.cw-version-row--selected) > td {
    background: #f8f4eb !important;
}
.cw-versions-card :deep(.ant-table-tbody > tr) {
    cursor: pointer;
}
.version-name-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.version-name-cell__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
}
.version-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}
.version-tags :deep(.ant-tag),
.version-inline-tag.ant-tag {
    margin: 0;
    border-radius: 999px;
    font-size: 12px;
    line-height: 20px;
    padding-inline: 8px;
}
.version-display-name {
    font-weight: 600;
    color: #0f172a;
}
.version-created {
    font-size: 13px;
    color: #64748b;
    font-variant-numeric: tabular-nums;
}
.amount-cell {
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum';
    color: #334155;
}
.amount-cell--positive {
    color: #15803d;
}
.amount-cell--negative {
    color: #dc2626;
}
.amount-cell--neutral {
    color: #64748b;
}
</style>

<style>
.cw-client-detail .cw-btn.ant-btn {
    height: 36px;
    padding-inline: 16px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.01em;
    box-shadow: 0 1px 2px rgba(43, 43, 43, 0.04);
    transition:
        transform 0.15s ease,
        box-shadow 0.15s ease,
        background 0.15s ease,
        border-color 0.15s ease;
}
.cw-client-detail .cw-btn--start.ant-btn {
    height: 42px;
    padding-inline: 20px;
    font-size: 14px;
}
.cw-client-detail .cw-btn--primary.ant-btn-primary {
    border-color: #8b6914;
    color: #fffdf8;
    background: linear-gradient(180deg, #d4b56a 0%, #8b6914 100%);
    box-shadow: 0 4px 14px rgba(139, 105, 20, 0.22);
}
.cw-client-detail .cw-btn--primary.ant-btn-primary:not(:disabled):hover {
    border-color: #6b5428;
    background: linear-gradient(180deg, #c4a35a 0%, #6b5428 100%);
    box-shadow: 0 6px 18px rgba(139, 105, 20, 0.32);
    transform: translateY(-1px);
}
.cw-client-detail .cw-btn--secondary.ant-btn-default {
    border-color: #d6d0c4;
    color: #4a453c;
    background: #fff;
}
.cw-client-detail .cw-btn--secondary.ant-btn-default:not(:disabled):hover {
    border-color: #c4a35a;
    color: #8b6914;
    background: #f6f1e6;
}
.cw-client-detail .cw-btn--danger.ant-btn-dangerous {
    border-color: #fca5a5;
    color: #dc2626;
    background: #fff;
}
.cw-client-detail .cw-btn--danger.ant-btn-dangerous:not(:disabled):hover {
    border-color: #f87171;
    color: #b91c1c;
    background: #fef2f2;
}
</style>
